/**
 * Shared date-field constraints.
 *
 * "Past-only" dates (incident / loss / purchase) can only be today or
 * earlier, so their pickers should block future dates. This is driven by
 * per-field config (`configuration_data.disable_future` / `allow_future`)
 * with a default set of well-known past-only `code_name`s, so the behaviour
 * applies without reseeding while staying fully tenant-agnostic and
 * overridable per tenant via config.
 */

/** Well-known date fields that are today-or-earlier by default. */
export const PAST_ONLY_DATE_CODES = new Set([
  'date_of_incident',
  'incident_date',
  'date_of_loss',
  'loss_date',
  'purchase_date',
  'date_of_purchase',
])

/**
 * Decide whether a date field should disallow future dates.
 *
 * `configuration_data.allow_future === true` always wins (opt-out), then an
 * explicit `configuration_data.disable_future === true` (opt-in), then the
 * default past-only `code_name` allowlist. No tenant slugs involved - a
 * tenant tunes this purely through the attribute's `configuration_data`.
 */
export function shouldDisableFutureDates(
  codeName: string | undefined,
  configurationData?: Record<string, unknown> | null,
): boolean {
  const cfg = (configurationData ?? {}) as Record<string, unknown>
  if (cfg.allow_future === true) return false
  const code = (codeName ?? '').toLowerCase()
  return cfg.disable_future === true || PAST_ONLY_DATE_CODES.has(code)
}
